import { useState } from 'react';
import {
    publishPricingDraft,
    rollbackPricingConfig,
    savePricingDraft,
    validatePricingDraft
} from '../api/pricingConfigApi';

export const pricingScopeLabel = (scope) => ({
    homePhone: 'Home Phone',
    internet: 'Internet',
    mobility: 'Mobility',
    security: 'Security',
    addons: 'Add-ons',
    tvDisplaySettings: 'TV Display Settings',
    emailShell: 'Email Shell'
}[scope] || 'Home Phone');

export const useDraftWorkflow = ({ draftConfig, draftStatus, loadConfig }) => {
    const [draftMessage, setDraftMessage] = useState('');
    const [draftError, setDraftError] = useState('');
    const [draftValidation, setDraftValidation] = useState(null);
    const [validatingDraft, setValidatingDraft] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [publishScope, setPublishScope] = useState('homePhone');
    const [publishSummary, setPublishSummary] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [rollbackTarget, setRollbackTarget] = useState(null);
    const [rollbackScope, setRollbackScope] = useState('homePhone');
    const [rollbackSummary, setRollbackSummary] = useState('');
    const [rollingBack, setRollingBack] = useState(false);

    const clearFeedback = () => {
        setDraftError('');
        setDraftMessage('');
    };

    const validateDraftConfig = async () => {
        if (!draftConfig) return;
        setValidatingDraft(true);
        setDraftValidation(null);
        clearFeedback();
        try {
            const { response, data } = await validatePricingDraft(draftConfig);
            if (!response.ok) {
                throw new Error(data.error || `Draft validation failed with status ${response.status}`);
            }
            setDraftValidation(data);
            setDraftMessage(data.valid ? 'Draft config validation passed.' : 'Draft config validation found issues.');
        } catch (error) {
            setDraftError(error.message || 'Failed to validate draft config.');
        } finally {
            setValidatingDraft(false);
        }
    };

    const saveScopedDraftConfig = async (nextConfig, scope, changeSummary) => {
        const { response, data } = await savePricingDraft(nextConfig, scope, changeSummary);
        if (!response.ok || data.saved === false) {
            const validationErrors = data.validation?.errors?.join(' ') || data.error;
            throw new Error(validationErrors || `${pricingScopeLabel(scope)} draft save failed with status ${response.status}`);
        }
        await loadConfig({ showLoading: false });
        return data;
    };

    const openPublishDialog = (scope = 'homePhone') => {
        clearFeedback();
        setPublishScope(scope);
        setPublishSummary(draftStatus?.changeSummary || `Publish ${pricingScopeLabel(scope)} draft.`);
        setPublishDialogOpen(true);
    };

    const publishDraft = async () => {
        const summary = publishSummary.trim();
        if (!summary) {
            setDraftError('A change summary is required before publishing.');
            return;
        }
        setPublishing(true);
        clearFeedback();
        try {
            const { response, data } = await publishPricingDraft(publishScope, summary);
            if (!response.ok || data.published === false) {
                const validationErrors = data.validation?.errors?.join(' ') || data.error;
                throw new Error(validationErrors || `Publish failed with status ${response.status}`);
            }
            setPublishDialogOpen(false);
            setDraftValidation(data.validation || null);
            setDraftMessage(`${pricingScopeLabel(publishScope)} draft published as active version ${data.storage?.active?.versionNumber || 'new'}.`);
            await loadConfig({ showLoading: false });
        } catch (error) {
            setDraftError(error.message || `Failed to publish ${pricingScopeLabel(publishScope)} draft.`);
        } finally {
            setPublishing(false);
        }
    };

    const openRollbackDialog = (version, scope = 'homePhone') => {
        clearFeedback();
        setRollbackTarget(version);
        setRollbackScope(scope);
        setRollbackSummary(`Rollback ${pricingScopeLabel(scope)} config to version ${version.versionNumber}.`);
    };

    const rollbackConfig = async () => {
        if (!rollbackTarget) return;
        setRollingBack(true);
        clearFeedback();
        try {
            const { response, data } = await rollbackPricingConfig(
                rollbackScope,
                rollbackTarget.versionNumber,
                rollbackSummary.trim()
            );
            if (!response.ok || data.rolledBack === false) {
                const validationErrors = data.validation?.errors?.join(' ') || data.error;
                throw new Error(validationErrors || `Rollback failed with status ${response.status}`);
            }
            setRollbackTarget(null);
            setDraftValidation(data.validation || null);
            setDraftMessage(`${pricingScopeLabel(rollbackScope)} config rolled back into active version ${data.storage?.active?.versionNumber || 'new'}.`);
            await loadConfig({ showLoading: false });
        } catch (error) {
            setDraftError(error.message || `Failed to rollback ${pricingScopeLabel(rollbackScope)} config.`);
        } finally {
            setRollingBack(false);
        }
    };

    return {
        draftMessage,
        setDraftMessage,
        draftError,
        setDraftError,
        draftValidation,
        validatingDraft,
        validateDraftConfig,
        saveScopedDraftConfig,
        publishDialogOpen,
        setPublishDialogOpen,
        publishScope,
        publishSummary,
        setPublishSummary,
        publishing,
        openPublishDialog,
        publishDraft,
        rollbackTarget,
        setRollbackTarget,
        rollbackScope,
        rollbackSummary,
        setRollbackSummary,
        rollingBack,
        openRollbackDialog,
        rollbackConfig
    };
};
